import React, { useEffect, useRef, useState } from 'react';
import { Animated, View, Text, StyleSheet, StyleProp, ViewStyle, Platform, Pressable } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { DLOSimpleTooltip, DLOTooltipTrigger } from '../../../modules/dlo/components/DLOTooltip';
import executiveOverviewCardStyles from '../styles/executiveOverviewCardStyles';
import { PRIMARY_BLUE, WARNING_RED } from '../constants/colors';
import { KpiMetric } from '../pages/PerfectTruckAnalytics/perfectTruckKpiApi';
import { RouteOutcomes } from '../pages/PerfectTruckAnalytics/perfectTruckKpiApi';
import { useTheme } from '../../../theme/useTheme';

interface ExecutiveOverviewCardProps {
  title: string;
  value?: string;
  subText?: string;
  infoText?: string;
  useCompletedFormat?: boolean;
  variant?: 'standard' | 'route-outcomes';
  routeOutcomes?: RouteOutcomes;
  loading?: boolean;
  percentage?: number;
  metric?: KpiMetric;
  containerStyle?: StyleProp<ViewStyle>;
  /** Overrides the computed red/blue value color (e.g. for neutral count KPIs). */
  valueColor?: string;
  /** When provided, renders an info icon next to the title that shows this
   * description on hover (web) or tap (mobile) — mirrors the KPI One Pager
   * table's cell tooltip pattern. */
  tooltip?: string;
  /** Controls how the tooltip is triggered. Use "click" on mobile (responsive
   * web narrow layout) so tapping the icon opens it; "hover" (default) for
   * desktop. */
  tooltipTrigger?: DLOTooltipTrigger;
}

const CardSkeleton: React.FC<{ variant?: 'standard' | 'route-outcomes' }> = ({ variant = 'standard' }) => {
  const shimmer = useRef(new Animated.Value(0)).current;
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmer, { toValue: 1, duration: 900, useNativeDriver: true }),
        Animated.timing(shimmer, { toValue: 0, duration: 900, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [shimmer]);

  const opacity = shimmer.interpolate({ inputRange: [0, 1], outputRange: [0.35, 0.7] });
  const skeletonBg = isDark ? '#363636' : '#D1D5DB';

  if (variant === 'route-outcomes') {
    return (
      <View style={[executiveOverviewCardStyles.card, isDark && cardDarkStyles.card]}>
        <Animated.View style={[skeletonStyles.titleBar, { opacity, backgroundColor: skeletonBg }]} />
        <View style={executiveOverviewCardStyles.outcomesRow}>
          {[0, 1, 2].map((i) => (
            <Animated.View key={i} style={[skeletonStyles.outcomeBlock, { opacity, backgroundColor: skeletonBg }]} />
          ))}
        </View>
      </View>
    );
  }

  return (
    <View style={[executiveOverviewCardStyles.card, isDark && cardDarkStyles.card]}>
      <Animated.View style={[skeletonStyles.titleBar, { opacity, backgroundColor: skeletonBg }]} />
      <Animated.View style={[skeletonStyles.valueBar, { opacity, backgroundColor: skeletonBg }]} />
      <Animated.View style={[skeletonStyles.subTextBar, { opacity, backgroundColor: skeletonBg }]} />
    </View>
  );
};

const skeletonStyles = StyleSheet.create({
  titleBar: {
    height: 12,
    width: '55%',
    borderRadius: 6,
    backgroundColor: '#D1D5DB',
    marginBottom: 8,
  },
  valueBar: {
    height: 36,
    width: '40%',
    borderRadius: 6,
    backgroundColor: '#D1D5DB',
    marginBottom: 6,
  },
  subTextBar: {
    height: 12,
    width: '70%',
    borderRadius: 6,
    backgroundColor: '#D1D5DB',
  },
  outcomeBlock: {
    flex: 1,
    height: 56,
    borderRadius: 8,
    backgroundColor: '#D1D5DB',
  },
});

/** Dark-mode overrides applied on top of the static light-mode styles. */
const cardDarkStyles = StyleSheet.create({
  card: {
    backgroundColor: '#202020',
    borderColor: '#404040',
    borderWidth: 1,
    // Suppress the light-mode shadow so it doesn't bleed on dark surfaces
    shadowOpacity: 0,
    elevation: 0,
  },
  cardTitle: {
    color: '#B0B0B0',
  },
  cardSubText: {
    color: '#8A9BB0',
  },
  approvedBox: {
    backgroundColor: '#0D2B52',
  },
  approvedText: {
    color: '#82B4FF',
  },
  pendingBox: {
    backgroundColor: '#2B2B2B',
  },
  pendingText: {
    color: '#B0B0B0',
  },
  rejectedBox: {
    backgroundColor: '#3D0A00',
  },
  rejectedText: {
    color: '#FF8A80',
  },
});

const ExecutiveOverviewCard: React.FC<ExecutiveOverviewCardProps> = ({
  title,
  value,
  subText,
  infoText,
  useCompletedFormat = false,
  variant = 'standard',
  routeOutcomes,
  loading = false,
  percentage,
  metric,
  containerStyle,
  valueColor: valueColorOverride,
  tooltip,
  tooltipTrigger = 'hover',
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const styles = executiveOverviewCardStyles;
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringRef = useRef(false);
  const tooltipVisibleAtRef = useRef<number | null>(null);
  const MIN_TOOLTIP_DURATION = 800;  // Prevent flicker during cursor movement

  const handleMouseEnter = () => {
    isHoveringRef.current = true;
    
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    
    // Debounce show by 200ms to avoid flicker from rapid hover events
    debounceTimerRef.current = setTimeout(() => {
      if (isHoveringRef.current) {
        tooltipVisibleAtRef.current = Date.now();
        setShowTooltip(true);
      }
    }, 200);
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
    
    // Check if tooltip has been visible for minimum duration
    if (tooltipVisibleAtRef.current !== null) {
      const visibleDuration = Date.now() - tooltipVisibleAtRef.current;
      if (visibleDuration < MIN_TOOLTIP_DURATION) {
        // Too soon to hide - delay instead
        tooltipTimerRef.current = setTimeout(() => {
          if (!isHoveringRef.current) {
            tooltipVisibleAtRef.current = null;
            setShowTooltip(false);
          }
        }, MIN_TOOLTIP_DURATION - visibleDuration);
        return;
      }
    }
    
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    
    tooltipVisibleAtRef.current = null;
    setShowTooltip(false);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  if (loading) {
    return <CardSkeleton variant={variant} />;
  }

  const renderTitle = () =>
    tooltip ? (
      <View style={styles.titleRow}>
        <Text style={styles.cardTitle}>{title}</Text>
        <DLOSimpleTooltip text={tooltip} position="top" trigger={tooltipTrigger}>
          <View style={styles.infoIconWrap} testID={`executive-overview-card-info-${title}`}>
            <MaterialIcons name="info-outline" size={14} color="#9AA1AB" />
          </View>
        </DLOSimpleTooltip>
      </View>
    ) : (
      <Text style={styles.cardTitle}>{title}</Text>
    );

  if (variant === 'route-outcomes') {
    // Guard against undefined/null percentage fields — the backend can omit
    // individual routeOutcomes fields for certain filter/chart-config combos
    // (e.g. hierarchyLevel/timeframeLevel overrides), which previously crashed
    // the app with "Cannot read properties of undefined (reading 'toFixed')".
    const fmt = (pct?: number | null) => (pct != null ? `${pct.toFixed(1)}%` : '—');

    const approvedPct = fmt(routeOutcomes?.approvedPercentage);
    const pendingPct  = fmt(routeOutcomes?.pendingPercentage);
    const declinedPct = fmt(routeOutcomes?.declinedPercentage);

    return (
      <View style={[styles.card, isDark && cardDarkStyles.card, containerStyle]}>
        <Text style={[styles.cardTitle, isDark && cardDarkStyles.cardTitle]}>{title}</Text>
        <View style={styles.outcomesRow}>
          <View style={[styles.outcomeBox, styles.approvedBox, isDark && cardDarkStyles.approvedBox]}>
            <Text style={[styles.outcomeValue, styles.approvedText, isDark && cardDarkStyles.approvedText]}>{approvedPct}</Text>
            <Text style={[styles.outcomeLabel, styles.approvedText, isDark && cardDarkStyles.approvedText]}>Approved</Text>
          </View>

          <View style={[styles.outcomeBox, styles.pendingBox, isDark && cardDarkStyles.pendingBox]}>
            <Text style={[styles.outcomeValue, styles.pendingText, isDark && cardDarkStyles.pendingText]}>{pendingPct}</Text>
            <Text style={[styles.outcomeLabel, styles.pendingText, isDark && cardDarkStyles.pendingText]}>Pending</Text>
          </View>

          <View style={[styles.outcomeBox, styles.rejectedBox, isDark && cardDarkStyles.rejectedBox]}>
            <Text style={[styles.outcomeValue, styles.rejectedText, isDark && cardDarkStyles.rejectedText]}>{declinedPct}</Text>
            <Text style={[styles.outcomeLabel, styles.rejectedText, isDark && cardDarkStyles.rejectedText]}>Declined</Text>
          </View>
        </View>
      </View>
    );
  }

  const pct = percentage ?? metric?.percentage;
  // In dark mode the semantic brand/warning colors stay readable on a dark card,
  // so no remapping is needed — only the null-fallback comes from the static style.
  const valueColor = valueColorOverride ?? (pct != null ? (pct >= 100 ? PRIMARY_BLUE : WARNING_RED) : styles.cardValue.color);

  // Format subText using metric fields with "Completed" label (only if useCompletedFormat is true)
  const formatMetricSubText = (): string => {
    if (!metric || !useCompletedFormat) return subText ?? '';
    
    // If metric has numeratorValue, construct the formatted string with "Completed"
    if (metric.numeratorValue != null && metric.denominatorValue != null) {
      const formatNumber = (num: number): string => {
        return num.toLocaleString();
      };
      return `${formatNumber(metric.numeratorValue)} Completed / ${formatNumber(metric.denominatorValue)} Target`;
    }
    
    // Fallback to the provided subText if numerator/denominator not available
    return subText ?? '';
  };

  const displaySubText = formatMetricSubText();

  return (
    <View style={[styles.card, containerStyle]}>
      <View style={styles.cardTitleRow}>
        <Text style={styles.cardTitle}>{title}</Text>
      </View>
      {/* Info icon positioned to top-right corner */}
      {infoText && (
        <Pressable
          onPress={() => setShowTooltip(!showTooltip)}
          onMouseEnter={Platform.OS === 'web' ? handleMouseEnter : undefined}
          onMouseLeave={Platform.OS === 'web' ? handleMouseLeave : undefined}
          style={styles.infoIconTopRight}
          accessibilityLabel="Information"
          accessibilityHint={infoText}
        >
          <MaterialIcons name="info" size={16} color="#9E9E9E" />
        </Pressable>
      )}
      {showTooltip && infoText && (
        <View style={styles.tooltipBox}>
          <Text style={styles.tooltipText}>{infoText}</Text>
        </View>
      )}
      <View style={styles.contentWrapper}>
        <Text style={[styles.cardValue, { color: valueColor }]}>{value ?? '—'}</Text>
        <Text style={styles.cardSubText}>{displaySubText}</Text>
      </View>
    </View>
  );
};

export default ExecutiveOverviewCard;
